// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Work Order Catalog (types · domains · categories · AI guidance)
//
// src/components/work_orders/workOrderCatalog.js  (generated from the shared core — keep in sync with backend/config/workOrderCatalog.js)
// ONE source of truth for "what kind of work can FlowSense create", shared by the
// React app (src/components/work_orders/workOrderCatalog.js) and the Express API
// (backend/config/workOrderCatalog.js). Both files are generated from the same core —
// if you add a category, add it to BOTH (or regenerate) so validation stays in sync.
//
//   domain   = WHAT the work is on   (water network, sensor, well, road, facility, fleet, internal)
//   type     = WHAT KIND of work     (corrective, emergency, preventive, predictive, inspection, …)
//   category = the specific job      (Road Paving, Line Striping, Sensor Calibration, …)
//
// The "AI" here is explainable rule-based guidance (keyword classifier + readiness
// checklist). The backend combines it with the ML triage model (/triage-issue) and the
// dispatcher / parts models that already run when a work order is created.
// ─────────────────────────────────────────────────────────────────────────────

const PRIORITY_RANK = { low: 1, medium: 2, high: 3, critical: 4 };
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

/** Work order types — what kind of work it is. */
const WORK_ORDER_TYPES = {
    corrective: {
        label: 'Corrective repair', short: 'Corrective', color: 'orange', defaultPriority: 'medium',
        backendCategory: 'Corrective Maintenance', fieldWork: true, requiresLink: true, requiresDueDate: false,
        explain: 'Something is broken or not working properly and needs to be fixed.',
        needs: 'Describe the symptoms (what is wrong, since when, how bad) and link the asset / road / sensor.',
    },
    emergency: {
        label: 'Emergency response', short: 'Emergency', color: 'red', defaultPriority: 'critical',
        backendCategory: 'Emergency Repair', fieldWork: true, requiresLink: false, requiresDueDate: false,
        explain: 'Danger to the public, a service outage or flooding — crews go immediately.',
        needs: 'Give an exact location and the hazard. Priority must be High or Critical.',
    },
    preventive: {
        label: 'Preventive maintenance', short: 'Preventive', color: 'green', defaultPriority: 'low',
        backendCategory: 'Preventive Maintenance', fieldWork: true, requiresLink: true, requiresDueDate: true,
        explain: 'Planned routine work that stops failures before they happen (servicing, exercising, cleaning).',
        needs: 'Link the asset and set the due date — preventive work is scheduled, not urgent.',
    },
    predictive: {
        label: 'Predictive (AI) maintenance', short: 'Predictive', color: 'purple', defaultPriority: 'high',
        backendCategory: 'Predictive Maintenance', fieldWork: true, requiresLink: true, requiresDueDate: true,
        explain: 'Raised from an AI failure prediction or sensor trend before the asset actually fails.',
        needs: 'Link the asset the prediction is about and schedule before the predicted failure date.',
    },
    inspection: {
        label: 'Inspection / survey', short: 'Inspection', color: 'cyan', defaultPriority: 'medium',
        backendCategory: 'Inspection', fieldWork: true, requiresLink: true, requiresDueDate: true,
        explain: 'Check the condition of something and record findings. Findings can raise follow-up work orders.',
        needs: 'Say what to inspect and what to check (the scope), link the asset / road / well and set a date.',
    },
    installation: {
        label: 'New installation', short: 'Install', color: 'blue', defaultPriority: 'medium',
        backendCategory: 'Installation', fieldWork: true, requiresLink: false, requiresDueDate: true,
        explain: 'Install something new — a meter, sensor, sign, speed hump, service connection.',
        needs: 'Give the location and what is being installed. Link the asset once it exists in FlowSense.',
    },
    replacement: {
        label: 'Replacement', short: 'Replace', color: 'pink', defaultPriority: 'medium',
        backendCategory: 'Replacement', fieldWork: true, requiresLink: true, requiresDueDate: true,
        explain: 'Replace an asset or component that is at the end of its life.',
        needs: 'Link the asset being replaced and note the replacement model / size.',
    },
    project: {
        label: 'Capital project / programme', short: 'Project', color: 'teal', defaultPriority: 'medium',
        backendCategory: 'Capital Project', fieldWork: true, requiresLink: false, requiresDueDate: true,
        explain: 'Larger planned works — a resurfacing programme, main replacement, new well field.',
        needs: 'Describe the scope and limits (from / to), and set the target completion date.',
    },
    internal_task: {
        label: 'Internal task', short: 'Internal', color: 'gray', defaultPriority: 'low',
        backendCategory: 'Internal', fieldWork: false, requiresLink: false, requiresDueDate: false,
        explain: 'Office / non-field work — data fixes, GIS updates, procurement, reports, training.',
        needs: 'Describe the task and the expected outcome.',
    },
};

/**
 * Domains — what the work is on. linkField = the FlowSense id that ties the job to its record.
 * Categories carry keywords for the classifier and a crew checklist for the AI guide.
 */
const WORK_ORDER_DOMAINS = {
    water_network: {
        label: 'Water network asset', short: 'Water', color: 'blue', icon: 'water',
        linkField: 'asset_id', linkLabel: 'Asset ID', linkRequired: true,
        explain: 'Pumps, valves, pipes / mains, hydrants, meters, tanks and treatment equipment.',
        keywords: ['water', 'pipe', 'main', 'valve', 'pump', 'hydrant', 'meter', 'tank', 'reservoir', 'pressure', 'leak', 'burst', 'chlorine', 'supply', 'service line'],
        types: ['corrective', 'emergency', 'preventive', 'predictive', 'inspection', 'installation', 'replacement', 'project'],
        categories: [
            { label: 'Leak Repair', type: 'corrective', priority: 'high', keywords: ['leak', 'leaking', 'drip', 'seep', 'wet'], checklist: ['Isolation valves located', 'Repair clamp / coupling of the right size', 'Traffic control if in the road'] },
            { label: 'Main Break', type: 'emergency', priority: 'critical', keywords: ['burst', 'main break', 'broken main', 'gushing', 'no water'], checklist: ['Shut-off plan and customers affected', 'Excavator + shoring', 'Notify residents of the outage'] },
            { label: 'Valve Repair / Exercise', type: 'preventive', priority: 'medium', keywords: ['valve', 'seized', 'stuck valve', 'exercise'], checklist: ['Valve key and size', 'Turns to close recorded', 'Packing kit'] },
            { label: 'Pump Maintenance', type: 'preventive', priority: 'medium', keywords: ['pump', 'motor', 'vibration', 'bearing', 'impeller', 'cavitation'], checklist: ['Lock-out / tag-out', 'Vibration and amp readings before / after', 'Bearings, seals, lubricant'] },
            { label: 'Hydrant Service', type: 'preventive', priority: 'low', keywords: ['hydrant', 'fire hydrant'], checklist: ['Flow test', 'Caps and threads', 'Paint / marking'] },
            { label: 'Meter Install / Replace', type: 'installation', priority: 'low', keywords: ['meter', 'new meter', 'replace meter', 'meter box'], checklist: ['Meter size and serial', 'Account number', 'Final / initial reading'] },
            { label: 'Meter Reading Issue', type: 'inspection', priority: 'low', keywords: ['reading', 'high bill', 'meter not reading', 'estimated'], checklist: ['Current reading + photo', 'Leak check (dial test)', 'Account history'] },
            { label: 'Pipe Replacement', type: 'replacement', priority: 'medium', keywords: ['replace pipe', 'old pipe', 'corroded', 'asbestos', 'galvanised'], checklist: ['Length, diameter and material', 'Permits / road opening', 'Reinstatement plan'] },
            { label: 'Water Quality / Flushing', type: 'corrective', priority: 'high', keywords: ['brown water', 'dirty water', 'taste', 'odour', 'odor', 'flushing', 'quality', 'turbid'], checklist: ['Sample bottles', 'Chlorine residual test', 'Flush point and duration'] },
            { label: 'Low Pressure', type: 'corrective', priority: 'medium', keywords: ['low pressure', 'pressure', 'weak flow', 'no pressure'], checklist: ['Pressure gauge reading', 'Nearby valve positions', 'PRV status'] },
            { label: 'Service Connection', type: 'installation', priority: 'low', keywords: ['connection', 'new service', 'tap', 'service line'], checklist: ['Approved application', 'Tapping saddle + corporation stop', 'Meter box location'] },
            { label: 'Storage Tank Inspection', type: 'inspection', priority: 'medium', keywords: ['tank', 'reservoir', 'storage'], checklist: ['Confined-space permit', 'Structural / coating condition', 'Level sensor check'] },
        ],
    },
    sensor: {
        label: 'Sensor / IoT device', short: 'Sensor', color: 'purple', icon: 'sensor',
        linkField: 'sensor_id', linkLabel: 'Sensor / device ID', linkRequired: true,
        explain: 'Pressure, flow, level and water-quality sensors, gateways and telemetry nodes.',
        keywords: ['sensor', 'device', 'telemetry', 'iot', 'gateway', 'node', 'battery', 'signal', 'offline', 'calibration', 'reading spike', 'firmware'],
        types: ['corrective', 'preventive', 'predictive', 'inspection', 'installation', 'replacement'],
        categories: [
            { label: 'Sensor Calibration', type: 'preventive', priority: 'medium', keywords: ['calibrat', 'drift', 'wrong reading', 'offset'], checklist: ['Reference instrument', 'Before / after readings', 'Update calibration date in FlowSense'] },
            { label: 'Battery Replacement', type: 'preventive', priority: 'medium', keywords: ['battery', 'low battery', 'power'], checklist: ['Correct battery pack', 'Seal / gasket', 'Confirm device reports after swap'] },
            { label: 'Connectivity Fault', type: 'corrective', priority: 'high', keywords: ['offline', 'no signal', 'not reporting', 'connectivity', 'gateway', 'lora', 'sim'], checklist: ['Signal strength reading', 'Antenna / SIM check', 'Last-seen time from FlowSense'] },
            { label: 'Data Anomaly Check', type: 'inspection', priority: 'medium', keywords: ['anomaly', 'spike', 'strange data', 'flatline', 'outlier'], checklist: ['Compare with a manual reading', 'Check for physical damage / fouling', 'Note if the anomaly is real (leak, flood) or a fault'] },
            { label: 'Sensor Replacement', type: 'replacement', priority: 'medium', keywords: ['replace sensor', 'damaged sensor', 'broken sensor', 'dead sensor'], checklist: ['Replacement model + serial', 'Re-register device ID', 'Commission and verify data'] },
            { label: 'Sensor Installation', type: 'installation', priority: 'low', keywords: ['install sensor', 'new sensor', 'mount'], checklist: ['Mounting hardware', 'Register in FlowSense with the asset it monitors', 'First reading verified'] },
            { label: 'Firmware Update', type: 'preventive', priority: 'low', keywords: ['firmware', 'update', 'ota'], checklist: ['Firmware version', 'Rollback plan', 'Confirm reporting after update'] },
        ],
    },
    well: {
        label: 'Drainage / flood well', short: 'Well', color: 'cyan', icon: 'well',
        linkField: 'well_id', linkLabel: 'Well ID', linkRequired: true,
        explain: 'Drainage and flood-relief wells, soak-aways and their pumps and grates.',
        keywords: ['well', 'drain well', 'flood', 'flooding', 'siltation', 'silt', 'desilt', 'soakaway', 'sump', 'ponding', 'standing water', 'grate'],
        types: ['corrective', 'emergency', 'preventive', 'predictive', 'inspection', 'installation', 'project'],
        categories: [
            { label: 'Well Clearance / Desilting', type: 'preventive', priority: 'medium', keywords: ['desilt', 'silt', 'clearance', 'blocked well', 'clogged'], checklist: ['Vac-truck booked', 'Depth before / after (siltation %)', 'Debris disposal'] },
            { label: 'Pump-out / Dewatering', type: 'emergency', priority: 'critical', keywords: ['pump out', 'dewater', 'overflow', 'surcharge', 'overtopping'], checklist: ['Pump + hoses', 'Discharge point', 'Water level reading from the well sensor'] },
            { label: 'Grate / Cover Repair', type: 'corrective', priority: 'high', keywords: ['grate', 'cover', 'missing cover', 'broken grate'], checklist: ['Grate size', 'Temporary barrier', 'Photo before / after'] },
            { label: 'Siltation Survey', type: 'inspection', priority: 'low', keywords: ['survey', 'depth', 'measure well'], checklist: ['Measured depth vs design depth', 'Photo', 'Update siltation in FlowSense'] },
            { label: 'Flood Response', type: 'emergency', priority: 'critical', keywords: ['flood', 'flooding', 'ponding', 'standing water', 'road flooded'], checklist: ['Road closure / signage', 'Pump-out crew', 'Check the AI flood prediction for nearby wells'] },
            { label: 'Well Construction', type: 'project', priority: 'medium', keywords: ['new well', 'construct well', 'drill'], checklist: ['Design depth', 'Permits', 'Register the new well in FlowSense'] },
        ],
    },
    road: {
        label: 'Road & streets', short: 'Road', color: 'orange', icon: 'road',
        linkField: 'road_id', linkLabel: 'Road / segment ID', linkRequired: true,
        explain: 'Paving, potholes, road markings and striping, signs, signals, curbs, sidewalks and road drainage.',
        keywords: ['road', 'street', 'pavement', 'asphalt', 'pothole', 'paving', 'resurface', 'marking', 'striping', 'line', 'crosswalk', 'sign', 'signal', 'curb', 'kerb', 'sidewalk', 'shoulder', 'speed hump', 'guardrail', 'streetlight'],
        types: ['corrective', 'emergency', 'preventive', 'predictive', 'inspection', 'installation', 'replacement', 'project'],
        categories: [
            // NOTE: 'Road Paving' and 'Pothole Repair' are exact strings the backend uses
            // (4D dig-once conflict check on create, last_paved_date on completion).
            { label: 'Road Paving', type: 'project', priority: 'medium', keywords: ['paving', 'pave', 'resurface', 'resurfacing', 'overlay', 'asphalt', 'mill and fill'], checklist: ['Limits (from / to) and area m²', 'Dig-once check: no utility works planned underneath', 'Traffic management plan', 'Asphalt tonnage'] },
            { label: 'Pothole Repair', type: 'corrective', priority: 'high', keywords: ['pothole', 'pot hole', 'hole in road', 'crater'], checklist: ['Size (L × W × depth)', 'Cold / hot mix', 'Cone the area', 'Photo before / after'] },
            { label: 'Road Marking', type: 'preventive', priority: 'medium', keywords: ['marking', 'road marking', 'arrow', 'faded marking', 'symbol'], checklist: ['Marking type (arrow, text, symbol)', 'Thermoplastic / paint', 'Lane closure'] },
            { label: 'Line Striping', type: 'preventive', priority: 'medium', keywords: ['striping', 'stripe', 'lane line', 'centre line', 'center line', 'edge line', 'faded lines'], checklist: ['Linear metres and colour', 'Line type (solid / dashed / double)', 'Striping truck + glass beads', 'Drying time / traffic control'] },
            { label: 'Crosswalk / Stop Bar', type: 'preventive', priority: 'medium', keywords: ['crosswalk', 'zebra', 'pedestrian crossing', 'stop bar', 'stop line'], checklist: ['Crossing dimensions', 'Thermoplastic', 'School / pedestrian peak times avoided'] },
            { label: 'Signage Install / Repair', type: 'corrective', priority: 'medium', keywords: ['sign', 'signage', 'stop sign', 'street sign', 'knocked down sign'], checklist: ['Sign type and size', 'Post + fixings', 'Reflectivity check'] },
            { label: 'Traffic Signal Repair', type: 'emergency', priority: 'critical', keywords: ['traffic light', 'signal', 'lights out', 'stoplight'], checklist: ['Police / traffic control notified', 'Controller cabinet access', 'Bulb / LED module spares'] },
            { label: 'Curb & Sidewalk Repair', type: 'corrective', priority: 'medium', keywords: ['curb', 'kerb', 'sidewalk', 'footpath', 'trip hazard'], checklist: ['Length and material', 'Pedestrian diversion', 'Concrete curing time'] },
            { label: 'Road Drainage / Culvert', type: 'corrective', priority: 'high', keywords: ['culvert', 'road drain', 'gully', 'catch basin', 'drainage'], checklist: ['Jetting / vac-truck', 'Outfall check', 'Link nearby drainage wells'] },
            { label: 'Crack Sealing', type: 'preventive', priority: 'low', keywords: ['crack', 'cracking', 'seal crack'], checklist: ['Crack length', 'Sealant', 'Dry weather window'] },
            { label: 'Utility Cut Reinstatement', type: 'corrective', priority: 'medium', keywords: ['reinstatement', 'utility cut', 'trench', 'patch after dig'], checklist: ['Original work order / permit', 'Compaction test', 'Matching surface'] },
            { label: 'Speed Hump Installation', type: 'installation', priority: 'low', keywords: ['speed hump', 'speed bump', 'traffic calming'], checklist: ['Approved location', 'Signs + markings for the hump', 'Drainage around the hump'] },
            { label: 'Guardrail Repair', type: 'corrective', priority: 'high', keywords: ['guardrail', 'barrier', 'crash barrier'], checklist: ['Damaged length', 'Posts and rail sections', 'Lane closure'] },
            { label: 'Streetlight Repair', type: 'corrective', priority: 'medium', keywords: ['streetlight', 'street light', 'lamp', 'light out'], checklist: ['Pole number', 'Bucket truck', 'Lamp / LED driver'] },
            { label: 'Shoulder / Verge Maintenance', type: 'preventive', priority: 'low', keywords: ['shoulder', 'verge', 'overgrown', 'grass cutting', 'edge drop'], checklist: ['Length', 'Mower / grader', 'Debris disposal'] },
            { label: 'Road Condition Survey', type: 'inspection', priority: 'low', keywords: ['condition survey', 'road survey', 'pci', 'assessment'], checklist: ['Condition rating (PCI)', 'Photos every 50 m', 'Record the assessment in Road Works'] },
        ],
    },
    facility: {
        label: 'Facility / building', short: 'Facility', color: 'yellow', icon: 'facility',
        linkField: 'asset_id', linkLabel: 'Facility asset ID', linkRequired: false,
        explain: 'Pump stations, depots, offices, treatment-plant buildings, generators and grounds.',
        keywords: ['building', 'office', 'depot', 'station', 'generator', 'hvac', 'air condition', 'ac unit', 'roof', 'electrical', 'plumbing', 'security', 'fence', 'gate', 'grounds'],
        types: ['corrective', 'emergency', 'preventive', 'inspection', 'installation', 'replacement', 'project'],
        categories: [
            { label: 'Electrical', type: 'corrective', priority: 'high', keywords: ['electrical', 'power outage', 'breaker', 'wiring', 'socket'], checklist: ['Qualified electrician', 'Isolation / lock-out', 'Test certificate'] },
            { label: 'HVAC', type: 'corrective', priority: 'medium', keywords: ['hvac', 'air condition', 'ac unit', 'cooling', 'ventilation'], checklist: ['Unit model', 'Refrigerant', 'Filter'] },
            { label: 'Generator Service', type: 'preventive', priority: 'medium', keywords: ['generator', 'genset', 'backup power'], checklist: ['Run hours', 'Oil / filters', 'Load test'] },
            { label: 'Plumbing', type: 'corrective', priority: 'medium', keywords: ['plumbing', 'toilet', 'sink', 'drain blocked'], checklist: ['Fixture', 'Parts', 'Water shut-off'] },
            { label: 'Security / Access', type: 'corrective', priority: 'high', keywords: ['security', 'fence', 'gate', 'lock', 'break-in', 'cctv'], checklist: ['Site secured', 'Police report if theft', 'Replacement lock / fence panels'] },
            { label: 'Building Repair', type: 'corrective', priority: 'medium', keywords: ['roof', 'wall', 'door', 'window', 'building damage'], checklist: ['Damage photos', 'Materials', 'Safety barrier'] },
            { label: 'Grounds Maintenance', type: 'preventive', priority: 'low', keywords: ['grounds', 'grass', 'landscaping', 'clean up'], checklist: ['Area', 'Equipment', 'Waste disposal'] },
        ],
    },
    fleet: {
        label: 'Fleet & equipment', short: 'Fleet', color: 'gray', icon: 'fleet',
        linkField: 'asset_id', linkLabel: 'Vehicle / equipment ID', linkRequired: false,
        explain: 'Trucks, vac-trucks, excavators, compressors, pumps and tools used by crews.',
        keywords: ['truck', 'vehicle', 'vac truck', 'excavator', 'backhoe', 'compressor', 'tyre', 'tire', 'engine', 'equipment', 'tool'],
        types: ['corrective', 'preventive', 'inspection', 'replacement'],
        categories: [
            { label: 'Vehicle Service', type: 'preventive', priority: 'low', keywords: ['service', 'oil change', 'tyre', 'tire', 'brake'], checklist: ['Odometer / hours', 'Parts', 'Next service due'] },
            { label: 'Vehicle Breakdown', type: 'corrective', priority: 'high', keywords: ['breakdown', 'won\'t start', 'engine', 'broken down'], checklist: ['Location of vehicle', 'Tow needed?', 'Replacement vehicle for the crew'] },
            { label: 'Equipment Repair', type: 'corrective', priority: 'medium', keywords: ['equipment', 'compressor', 'excavator', 'backhoe', 'tool'], checklist: ['Equipment ID', 'Fault description', 'Parts'] },
            { label: 'Pre-use Safety Inspection', type: 'inspection', priority: 'low', keywords: ['safety check', 'pre-use', 'pre use', 'walkaround'], checklist: ['Checklist signed', 'Defects recorded', 'Out-of-service tag if unsafe'] },
        ],
    },
    internal: {
        label: 'Internal / admin task', short: 'Internal', color: 'gray', icon: 'internal',
        linkField: null, linkLabel: null, linkRequired: false,
        explain: 'Office work that supports the field — GIS / data fixes, procurement, permits, reports, training, IT.',
        keywords: ['report', 'data', 'gis', 'map update', 'procure', 'purchase', 'permit', 'training', 'it support', 'computer', 'account', 'meeting', 'admin'],
        types: ['internal_task', 'project', 'inspection'],
        categories: [
            { label: 'GIS / Mapping Update', type: 'internal_task', priority: 'low', keywords: ['gis', 'map', 'mapping', 'coordinates', 'unmapped'], checklist: ['Layer / asset to update', 'Source of the correct data', 'Verified in the map view'] },
            { label: 'Data Correction', type: 'internal_task', priority: 'low', keywords: ['data', 'correct record', 'wrong data', 'duplicate'], checklist: ['Record IDs', 'Correct values', 'Who requested it'] },
            { label: 'Procurement', type: 'internal_task', priority: 'medium', keywords: ['procure', 'purchase', 'order parts', 'quote', 'stock'], checklist: ['Items + quantities', 'Budget code', 'Supplier quotes'] },
            { label: 'Permit / Coordination', type: 'internal_task', priority: 'medium', keywords: ['permit', 'coordination', 'approval', 'utility coordination', 'road opening'], checklist: ['Agency', 'Dates', 'Linked field work order'] },
            { label: 'Report Preparation', type: 'internal_task', priority: 'low', keywords: ['report', 'kpi', 'presentation', 'summary'], checklist: ['Audience', 'Data period', 'Deadline'] },
            { label: 'Training', type: 'internal_task', priority: 'low', keywords: ['training', 'course', 'induction'], checklist: ['Attendees', 'Trainer', 'Certificate'] },
            { label: 'IT Support', type: 'internal_task', priority: 'medium', keywords: ['it support', 'computer', 'laptop', 'password', 'account', 'printer', 'tablet'], checklist: ['Device / account', 'Error message', 'Urgency for field crews'] },
        ],
    },
};

/** Where an inspection record can come from (for inspection_source). */
const INSPECTION_SOURCES = {
    work_order: 'FlowSense inspection work order',
    road_inspection: 'Road Works scheduled inspection',
    road_assessment: 'Road condition assessment',
    well_clearance: 'Well clearance inspection',
    asset_inspection: 'Asset inspection',
    sensor_check: 'Sensor field check',
    other: 'Other inspection',
};

/** Allowed work order sources (must match the backend route validator). */
const WORK_ORDER_SOURCES = ['manual', 'prediction', 'ai_sensor_alert', 'ai_prescriptive', 'scheduled', 'helpdesk'];

const LINK_FIELDS = ['asset_id', 'sensor_id', 'well_id', 'road_id'];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const isPresent = (v) => v !== null && v !== undefined && String(v).trim() !== '';
const lc = (v) => String(v == null ? '' : v).toLowerCase();

function getDomain(key) { return WORK_ORDER_DOMAINS[key] || null; }
function getType(key) { return WORK_ORDER_TYPES[key] || null; }

function categoriesFor(domainKey) {
    const d = getDomain(domainKey);
    return d ? d.categories : [];
}

function findCategory(label, domainKey) {
    if (!isPresent(label)) return null;
    const want = lc(label);
    const pools = domainKey && getDomain(domainKey) ? [[domainKey, getDomain(domainKey)]] : Object.entries(WORK_ORDER_DOMAINS);
    for (const [dk, d] of pools) {
        const c = d.categories.find(x => lc(x.label) === want);
        if (c) return { ...c, domain: dk };
    }
    return null;
}

function typesFor(domainKey) {
    const d = getDomain(domainKey);
    return d ? d.types : Object.keys(WORK_ORDER_TYPES);
}

/** Maps a FlowSense asset type ("Pump", "Valve", "Drainage Well", "Road") to a domain. */
function domainForAssetType(assetType) {
    const t = lc(assetType);
    if (!t) return null;
    if (/well|soak|sump|drain/.test(t)) return 'well';
    if (/road|street|segment|pavement/.test(t)) return 'road';
    if (/sensor|gateway|logger|telemetry|node/.test(t)) return 'sensor';
    if (/truck|vehicle|excavator|backhoe|fleet|compressor/.test(t)) return 'fleet';
    if (/building|office|depot|generator|hvac|facility/.test(t)) return 'facility';
    if (/pump|valve|pipe|main|hydrant|meter|tank|reservoir|plant|booster|prv|water/.test(t)) return 'water_network';
    return null;
}

/** Best-effort domain for an existing work order (older rows have no `domain`). */
function inferDomain(wo = {}) {
    if (isPresent(wo.domain) && WORK_ORDER_DOMAINS[wo.domain]) return wo.domain;
    if (isPresent(wo.road_id)) return 'road';
    if (isPresent(wo.well_id)) return 'well';
    if (isPresent(wo.sensor_id) && !isPresent(wo.asset_id)) return 'sensor';
    const cat = findCategory(wo.category);
    if (cat) return cat.domain;
    const fromAsset = domainForAssetType(wo.asset_type);
    if (fromAsset) return fromAsset;
    if (isPresent(wo.asset_id)) return 'water_network';
    return null;
}

/** Best-effort type for an existing work order. */
function inferType(wo = {}) {
    if (isPresent(wo.work_order_type) && WORK_ORDER_TYPES[wo.work_order_type]) return wo.work_order_type;
    const c = lc(wo.category);
    const byBackend = Object.entries(WORK_ORDER_TYPES).find(([, t]) => lc(t.backendCategory) === c);
    if (byBackend) return byBackend[0];
    if (['prediction', 'ai_prescriptive'].includes(wo.source)) return 'predictive';
    if (wo.source === 'scheduled') return 'preventive';
    const cat = findCategory(wo.category);
    if (cat) return cat.type;
    if (/inspect|survey|assessment/.test(lc(wo.title))) return 'inspection';
    return null;
}

/** Id + label of the FlowSense record a draft / work order is linked to. */
function linkOf(draft = {}) {
    const d = getDomain(draft.domain);
    if (d && d.linkField && isPresent(draft[d.linkField])) return { field: d.linkField, id: draft[d.linkField], label: d.linkLabel };
    for (const f of LINK_FIELDS) if (isPresent(draft[f])) return { field: f, id: draft[f], label: f.replace('_id', '') };
    return null;
}

const hasDraftLocation = (draft = {}) =>
    (isPresent(draft.latitude) && isPresent(draft.longitude)) || isPresent(draft.location_description) || isPresent(draft.address);

// ─────────────────────────────────────────────────────────────────────────────
// AI: keyword classifier (explainable — returns the words that matched)
// ─────────────────────────────────────────────────────────────────────────────
const EMERGENCY_WORDS = ['emergency', 'urgent', 'danger', 'dangerous', 'flood', 'flooding', 'burst', 'collapsed', 'sinkhole', 'live wire', 'no water', 'outage', 'accident', 'injury', 'gushing', 'lights out'];
const INSPECTION_WORDS = ['inspect', 'inspection', 'survey', 'check condition', 'assess', 'assessment', 'audit', 'look at', 'verify'];
const PREVENTIVE_WORDS = ['routine', 'scheduled', 'annual', 'monthly', 'quarterly', 'service', 'servicing', 'exercise', 'preventive', 'maintenance plan', 'repaint', 'refresh'];
const INSTALL_WORDS = ['install', 'installation', 'new ', 'add a', 'put in', 'mount'];
const REPLACE_WORDS = ['replace', 'replacement', 'end of life', 'worn out', 'swap'];

function suggestFromText(text, opts = {}) {
    const t = ` ${lc(text)} `;
    if (t.trim().length < 3) return null;
    const reasons = [];
    const matched = [];

    // Category: best keyword hit across allowed domains
    let best = null;
    const domainPool = opts.domain && getDomain(opts.domain) ? [[opts.domain, getDomain(opts.domain)]] : Object.entries(WORK_ORDER_DOMAINS);
    for (const [dk, d] of domainPool) {
        let domainHits = d.keywords.filter(k => t.includes(lc(k)));
        for (const c of d.categories) {
            const hits = c.keywords.filter(k => t.includes(lc(k)));
            if (!hits.length) continue;
            // symptom / fault categories (higher default priority) win ties over routine service ones
            const score = hits.reduce((s, k) => s + Math.min(3, k.split(' ').length + 1), 0) + domainHits.length * 0.5 + (PRIORITY_RANK[c.priority] || 2) * 0.75;
            if (!best || score > best.score) best = { score, domain: dk, category: c, hits, domainHits };
        }
        if (!best && domainHits.length && !opts.domain) {
            const score = domainHits.length * 0.5;
            if (!best || score > best.score) best = { score, domain: dk, category: null, hits: [], domainHits };
        }
    }
    if (best) {
        matched.push(...best.hits, ...best.domainHits);
        if (best.category) reasons.push(`"${best.hits[0]}" → ${best.category.label} (${getDomain(best.domain).short})`);
        else reasons.push(`Mentions ${best.domainHits.slice(0, 2).join(', ')} → ${getDomain(best.domain).label}`);
    }

    // Type: explicit words override the category default
    let type = best && best.category ? best.category.type : null;
    const firstHit = (list) => list.find(w => t.includes(w));
    const em = firstHit(EMERGENCY_WORDS);
    const insp = firstHit(INSPECTION_WORDS);
    const prev = firstHit(PREVENTIVE_WORDS);
    const inst = firstHit(INSTALL_WORDS);
    const repl = firstHit(REPLACE_WORDS);
    if (em) { type = 'emergency'; reasons.push(`"${em.trim()}" → emergency response`); matched.push(em.trim()); }
    else if (insp) { type = 'inspection'; reasons.push(`"${insp.trim()}" → inspection`); matched.push(insp.trim()); }
    else if (repl) { type = 'replacement'; reasons.push(`"${repl.trim()}" → replacement`); matched.push(repl.trim()); }
    else if (inst && !type) { type = 'installation'; reasons.push(`"${inst.trim()}" → new installation`); }
    else if (prev && !type) { type = 'preventive'; reasons.push(`"${prev.trim()}" → preventive`); }
    const domain = best ? best.domain : (opts.domain || null);
    if (domain && type && !typesFor(domain).includes(type)) type = typesFor(domain)[0];

    // Priority
    let priority = (best && best.category && best.category.priority) || (type && WORK_ORDER_TYPES[type].defaultPriority) || 'medium';
    if (type === 'emergency') priority = 'critical';
    if (/school|hospital|clinic|main road|highway|airport/.test(t) && PRIORITY_RANK[priority] < PRIORITY_RANK.high) {
        priority = 'high'; reasons.push('Sensitive location (school / hospital / main road) → at least High');
    }

    const confidence = best ? Math.min(95, Math.round(40 + best.score * 12)) : (type ? 45 : 0);
    if (!domain && !type) return null;
    return {
        domain, type, category: best && best.category ? best.category.label : null, priority,
        confidence, matched: Array.from(new Set(matched)).slice(0, 6), reasons,
        method: 'keyword rules',
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// AI: "how do I create this work order?" — step-by-step readiness check
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @returns {{ ready:boolean, score:number, steps:Array<{id,label,status,level,why,hint}>, missingRequired:Array, warnings:Array }}
 * status: ok | missing | warn ; level: required | recommended
 */
function validateDraft(draft = {}) {
    const steps = [];
    const domain = getDomain(draft.domain);
    const type = getType(draft.work_order_type);
    const step = (id, label, ok, level, why, hint, warn) => steps.push({ id, label, status: ok ? (warn ? 'warn' : 'ok') : 'missing', level, why, hint: hint || null, warning: warn || null });

    step('domain', 'Choose what the work is on', !!domain, 'required',
        'Decides which FlowSense record it links to, which crews and parts are relevant, and which AI models learn from it.',
        domain ? domain.label : 'Water asset, sensor, well, road, facility, fleet or internal.');
    step('type', 'Choose the work order type', !!type, 'required',
        'Sets the default priority, SLA, whether a due date is needed and how the job is reported (repair vs inspection vs project).',
        type ? type.explain : 'Corrective, emergency, preventive, predictive, inspection, installation, replacement, project or internal.');
    const typeOk = !domain || !type || domain.types.includes(draft.work_order_type);
    if (domain && type && !typeOk) step('type_fit', 'Type fits the domain', false, 'required', `${type.label} is not used for ${domain.label.toLowerCase()} work.`, `Use one of: ${domain.types.map(k => WORK_ORDER_TYPES[k].short).join(', ')}.`);

    const cat = findCategory(draft.category, draft.domain);
    step('category', 'Pick the job category', isPresent(draft.category), 'recommended',
        'The category drives the crew checklist, parts suggestions and (for paving) the dig-once utility-conflict check.',
        cat ? `Crew will need: ${cat.checklist.slice(0, 2).join('; ')}.` : (domain ? `e.g. ${domain.categories.slice(0, 3).map(c => c.label).join(', ')}` : null));

    // Link
    const needsLink = !!(domain && domain.linkField && domain.linkRequired && !['emergency', 'installation', 'project'].includes(draft.work_order_type));
    const link = linkOf(draft);
    if (domain && domain.linkField) {
        const hasLink = isPresent(draft[domain.linkField]);
        const locFallback = !hasLink && hasDraftLocation(draft) && draft.domain === 'road';
        step('link', `Link the ${domain.linkLabel.replace(' ID', '').toLowerCase()}`, hasLink || locFallback || !needsLink, needsLink ? 'required' : 'recommended',
            draft.domain === 'sensor' ? 'The sensor ID ties the job to the device\'s telemetry, alerts and calibration history.'
                : draft.domain === 'road' ? 'Linking the road segment updates its condition, paving date and work history, and feeds the road RUL model.'
                    : draft.domain === 'well' ? 'Linking the well updates its siltation / clearance history and the flood-risk model.'
                        : 'Linking the asset adds this job to its maintenance history so the failure-prediction model learns from it.',
            hasLink ? `${domain.linkLabel}: ${draft[domain.linkField]}` : (locFallback ? 'Using the location until the segment is mapped.' : `Enter the ${domain.linkLabel}.`),
            locFallback ? 'No road segment linked — the location will be used, but link the segment when it is mapped.' : null);
    }

    step('title', 'Write a clear title', isPresent(draft.title) && String(draft.title).trim().length >= 3, 'required',
        'Shown on every list, the mobile app and the citizen update.',
        cat && link ? `e.g. "${cat.label} – ${link.id}"` : 'What + where, e.g. "Line Striping – Bay St (East St to Victoria Ave)".');

    const descLen = String(draft.description || '').trim().length;
    const isInsp = draft.work_order_type === 'inspection';
    step('description', isInsp ? 'Describe what to inspect (scope)' : 'Describe the problem / work', descLen >= 15, type && type.fieldWork ? 'required' : 'recommended',
        isInsp ? 'Inspectors need the scope and what to check; findings are recorded as resolution notes and can raise follow-up work orders.'
            : 'The dispatcher matches technician skills from it and the parts AI reads it to suggest spares before the crew leaves.',
        type ? type.needs : null);

    if (type && type.fieldWork) {
        step('location', 'Add the location', hasDraftLocation(draft) || !!link, 'recommended',
            'The route optimizer batches nearby jobs and the dispatcher picks the closest crew — both need a location.',
            'Address / landmark, or GPS coordinates.');
    }

    const pr = lc(draft.priority);
    const prOk = PRIORITIES.includes(pr);
    const prWarn = prOk && draft.work_order_type === 'emergency' && PRIORITY_RANK[pr] < PRIORITY_RANK.high
        ? 'Emergency work should be High or Critical priority.' : null;
    step('priority', 'Set the priority', prOk && !prWarn, 'required',
        'Sets the SLA deadline the watchdog enforces and the dispatch order.',
        prOk ? `${pr} → SLA window set by the backend` : null, prWarn);
    if (prWarn) steps[steps.length - 1].status = 'warn';

    if (type && type.requiresDueDate) {
        step('due_date', 'Set the due date', isPresent(draft.due_date), 'required',
            `${type.short} work is scheduled — the due date drives the calendar, reminders and the watchdog.`, null);
    }

    if (isPresent(draft.parent_work_order_id)) {
        step('parent', 'Linked to its parent work order', true, 'recommended',
            isInsp ? 'This inspection is attached to the original job (e.g. QA after the repair).' : 'This job was raised from an inspection — the findings stay linked.',
            `Parent: ${draft.parent_work_order_id}`);
    }

    let total = 0, got = 0;
    steps.forEach(s => { const w = s.level === 'required' ? 2 : 1; total += w; if (s.status === 'ok') got += w; else if (s.status === 'warn') got += w / 2; });
    const score = total ? Math.round((got / total) * 100) : 0;
    const missingRequired = steps.filter(s => s.level === 'required' && s.status === 'missing');
    const warnings = steps.filter(s => s.status === 'warn').map(s => s.warning);
    return { ready: missingRequired.length === 0, score, steps, missingRequired, warnings };
}

/** Backend-side hard rules (only enforced when the caller sent a domain / type). */
function validateForCreate(body = {}) {
    const errors = [];
    if (isPresent(body.work_order_type) && !WORK_ORDER_TYPES[body.work_order_type]) errors.push(`Unknown work order type "${body.work_order_type}".`);
    if (isPresent(body.domain) && !WORK_ORDER_DOMAINS[body.domain]) errors.push(`Unknown domain "${body.domain}".`);
    const d = getDomain(body.domain);
    if (d && isPresent(body.work_order_type) && !d.types.includes(body.work_order_type)) {
        errors.push(`${WORK_ORDER_TYPES[body.work_order_type] ? WORK_ORDER_TYPES[body.work_order_type].label : body.work_order_type} is not used for ${d.label.toLowerCase()} work.`);
    }
    if (body.domain === 'sensor' && !isPresent(body.sensor_id)) errors.push('Sensor work orders need the sensor / device ID (sensor_id).');
    if (body.domain === 'well' && !isPresent(body.well_id) && !hasDraftLocation(body)) errors.push('Well work orders need the well ID (well_id) or a location.');
    if (body.work_order_type === 'emergency' && isPresent(body.priority) && PRIORITY_RANK[lc(body.priority)] < PRIORITY_RANK.high) errors.push('Emergency work orders must be High or Critical priority.');
    return errors;
}

/** Fills domain / type / category defaults the caller left out (keeps old callers working). */
function normalizeDraft(body = {}) {
    const out = { ...body };
    if (!isPresent(out.domain)) out.domain = inferDomain(out) || undefined;
    if (!isPresent(out.work_order_type)) {
        out.work_order_type = inferType(out)
            || (out.domain === 'internal' ? 'internal_task' : out.domain ? (typesFor(out.domain).includes('corrective') ? 'corrective' : typesFor(out.domain)[0]) : undefined);
    }
    if (!isPresent(out.category) && out.work_order_type && WORK_ORDER_TYPES[out.work_order_type]) out.category = WORK_ORDER_TYPES[out.work_order_type].backendCategory;
    return out;
}

function suggestTitle(draft = {}, entityName) {
    const cat = draft.category || (getType(draft.work_order_type) || {}).short || 'Work';
    const where = entityName || draft.location_description || (linkOf(draft) || {}).id;
    return where ? `${cat} – ${where}` : cat;
}

function descriptionTemplate(draft = {}) {
    const cat = findCategory(draft.category, draft.domain);
    const type = getType(draft.work_order_type);
    const lines = [];
    if (draft.work_order_type === 'inspection') {
        lines.push('Scope: <what to inspect, from / to>', 'Check for: <defects to look for>', 'Record: condition rating, photos, measurements.');
    } else if (type) {
        lines.push(draft.work_order_type === 'project' ? 'Scope / limits: <from – to, quantities>' : 'Problem / work required: <symptoms, since when>');
        lines.push('Access / safety: <traffic control, permits, hazards>');
    }
    if (cat) lines.push(`Crew checklist: ${cat.checklist.join('; ')}.`);
    return lines.join('\n');
}

/**
 * Pre-fills a create-work-order draft from anywhere in FlowSense.
 * @param {'asset'|'sensor'|'sensor_alert'|'well'|'road'|'road_inspection'|'inspection'|'prediction'|'helpdesk'|'internal'} source
 * @param {object} e  the record you are on (asset, sensor, well, road, alert, inspection WO …)
 */
function buildDraft(source, e = {}) {
    const base = { status: 'pending', source: 'manual' };
    switch (source) {
        case 'asset': {
            const domain = domainForAssetType(e.type || e.asset_type) || 'water_network';
            return { ...base, domain, work_order_type: 'corrective', asset_id: e.id ?? e.asset_id, latitude: e.latitude, longitude: e.longitude, location_description: e.location_description || e.address, title: '', _entityName: e.name || e.asset_name };
        }
        case 'sensor':
            return { ...base, domain: 'sensor', work_order_type: 'corrective', sensor_id: e.device_id ?? e.sensor_id ?? e.id, asset_id: e.asset_id, latitude: e.latitude, longitude: e.longitude, location_description: e.location_description, _entityName: e.name || e.device_id };
        case 'sensor_alert':
            return { ...base, domain: e.asset_id ? (domainForAssetType(e.asset_type) || 'water_network') : 'sensor', work_order_type: 'corrective', source: 'ai_sensor_alert', priority: ['critical', 'high'].includes(lc(e.severity)) ? lc(e.severity) : 'high', sensor_id: e.sensor_id ?? e.device_id, asset_id: e.asset_id, category: e.asset_id ? undefined : 'Data Anomaly Check', description: e.alert_message || e.message || '', _entityName: e.sensor_name || e.device_id };
        case 'well':
            return { ...base, domain: 'well', work_order_type: 'preventive', category: 'Well Clearance / Desilting', well_id: e.well_id ?? e.id, latitude: e.latitude, longitude: e.longitude, location_description: e.location_description || e.wellName || e.name, _entityName: e.wellName || e.name };
        case 'road':
            return { ...base, domain: 'road', work_order_type: 'corrective', road_id: e.id ?? e.road_id, location_description: e.name || e.road_name, latitude: e.latitude, longitude: e.longitude, _entityName: e.name || e.road_name };
        case 'road_inspection':
            return { ...base, domain: 'road', work_order_type: 'inspection', category: 'Road Condition Survey', road_id: e.road_id, inspection_ref_id: e.id, inspection_source: 'road_inspection', due_date: e.inspection_date || e.scheduled_date, _entityName: e.road_name };
        case 'inspection': // follow-up work from an inspection work order's findings
            return {
                ...base, domain: inferDomain(e) || undefined, work_order_type: 'corrective', parent_work_order_id: e.id,
                inspection_ref_id: e.id, inspection_source: 'work_order',
                asset_id: e.asset_id, road_id: e.road_id, sensor_id: e.sensor_id, well_id: e.well_id,
                latitude: e.latitude, longitude: e.longitude, location_description: e.location_description,
                description: isPresent(e.resolution_notes) ? `Raised from inspection #${e.id} findings:\n${e.resolution_notes}` : `Raised from inspection #${e.id}.`,
                priority: e.priority, _entityName: e.asset_name || e.road_name,
            };
        case 'qa_inspection': // inspection attached to an existing job (before or after the work)
            return {
                ...base, domain: inferDomain(e) || undefined, work_order_type: 'inspection', parent_work_order_id: e.id,
                category: inferDomain(e) === 'road' ? 'Road Condition Survey' : undefined,
                asset_id: e.asset_id, road_id: e.road_id, sensor_id: e.sensor_id, well_id: e.well_id,
                latitude: e.latitude, longitude: e.longitude, location_description: e.location_description,
                title: `Inspection: ${e.title || `Work order ${e.id}`}`,
                description: `Inspect the work done under work order #${e.id}${e.title ? ` (${e.title})` : ''}.\nCheck for: quality of the repair, safety, site left clean.\nRecord: condition, photos, any defects (raise follow-ups from them).`,
                priority: 'medium',
            };
        case 'prediction':
            return { ...base, domain: domainForAssetType(e.asset_type) || 'water_network', work_order_type: 'predictive', source: 'prediction', asset_id: e.asset_id ?? e.assetId, priority: (e.riskScore || e.risk_score) >= 80 ? 'high' : 'medium', due_date: e.predictedFailureDate ? String(e.predictedFailureDate).slice(0, 10) : undefined, _entityName: e.asset_name || e.assetName };
        case 'helpdesk':
            return { ...base, source: 'helpdesk', work_order_type: 'corrective', issue_id: e.id ?? e.ticket_id, description: e.description || e.message || '', latitude: e.latitude, longitude: e.longitude, location_description: e.address || e.location };
        case 'internal':
        default:
            return { ...base, domain: 'internal', work_order_type: 'internal_task' };
    }
}

/** Plain-data catalog (for GET /workorders/catalog and the UI). */
function catalogSnapshot() {
    return { types: WORK_ORDER_TYPES, domains: WORK_ORDER_DOMAINS, inspectionSources: INSPECTION_SOURCES, sources: WORK_ORDER_SOURCES, priorities: PRIORITIES };
}

const TYPE_KEYS = Object.keys(WORK_ORDER_TYPES);
const DOMAIN_KEYS = Object.keys(WORK_ORDER_DOMAINS);
const INSPECTION_SOURCE_KEYS = Object.keys(INSPECTION_SOURCES);

// ── Navigation helpers (open the create form from any FlowSense page) ──────
// --- UPDATE: the Work Orders page is mounted at /admin/workorders (src/routes.js); '/admin/work-orders'
// matched no route, so every "create work order" / "open work order" link from other pages landed on a blank page.
const CREATE_WORK_ORDER_PATH = process.env.REACT_APP_ROUTE_WORK_ORDERS || '/admin/workorders';
/** Link that opens one work order in the View modal: /admin/workorders?search=<id> */
function workOrderUrl(id) { return `${CREATE_WORK_ORDER_PATH}?search=${encodeURIComponent(id)}`; }
/** navigate(...) variant: opens the work order without a query string (state.openWorkOrderId). */
function openWorkOrder(navigate, id) { if (typeof navigate === 'function' && isPresent(id)) navigate(CREATE_WORK_ORDER_PATH, { state: { openWorkOrderId: String(id) } }); }
// --- END UPDATE

/** navigate('/admin/workorders', { state: { woDraft } }) — WorkOrderPage opens the create form pre-filled. */
function openCreateWorkOrder(navigate, draft = {}) {
    if (typeof navigate !== 'function') return;
    navigate(CREATE_WORK_ORDER_PATH, { state: { woDraft: draft } });
}

/** Shareable link: /admin/workorders?create=1&domain=road&road_id=12&category=Line%20Striping */
function createWorkOrderUrl(draft = {}) {
    const p = new URLSearchParams({ create: '1' });
    ['domain', 'work_order_type', 'category', 'priority', 'asset_id', 'sensor_id', 'well_id', 'road_id', 'parent_work_order_id', 'inspection_ref_id', 'inspection_source', 'title', 'location_description']
        .forEach(k => { if (isPresent(draft[k])) p.set(k, String(draft[k])); });
    return `${CREATE_WORK_ORDER_PATH}?${p.toString()}`;
}

/** Reads a draft back from ?create=1&… search params (null if not a create link). */
function draftFromSearchParams(sp) {
    if (!sp || sp.get('create') !== '1') return null;
    const d = {};
    ['domain', 'work_order_type', 'category', 'priority', 'asset_id', 'sensor_id', 'well_id', 'road_id', 'parent_work_order_id', 'inspection_ref_id', 'inspection_source', 'title', 'location_description']
        .forEach(k => { const v = sp.get(k); if (isPresent(v)) d[k] = v; });
    return d;
}

export {
    PRIORITIES,
    PRIORITY_RANK,
    WORK_ORDER_TYPES,
    WORK_ORDER_DOMAINS,
    INSPECTION_SOURCES,
    WORK_ORDER_SOURCES,
    LINK_FIELDS,
    TYPE_KEYS,
    DOMAIN_KEYS,
    INSPECTION_SOURCE_KEYS,
    getDomain,
    getType,
    categoriesFor,
    findCategory,
    typesFor,
    domainForAssetType,
    inferDomain,
    inferType,
    linkOf,
    suggestFromText,
    validateDraft,
    validateForCreate,
    normalizeDraft,
    suggestTitle,
    descriptionTemplate,
    buildDraft,
    catalogSnapshot,
    isPresent,
    CREATE_WORK_ORDER_PATH,
    openCreateWorkOrder,
    createWorkOrderUrl,
    draftFromSearchParams,
    workOrderUrl, // --- UPDATE
    openWorkOrder, // --- UPDATE
};